import React, { useState, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Box,
  Typography,
  Button,
  Checkbox,
  TextField,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Divider,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Description as AllRxIcon,
  Photo as RxImagesIcon,
  NoteAlt as NotesIcon,
  Timeline as TimelineIcon,
  History as HistoryIcon,
  LocalPharmacy as CounselingPointsIcon,
  Print as PrintIcon,
  Article as PatientEdIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  Medication as MedicationIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import type { DrugDrugInteraction, DuplicateTherapy, AllergyAlert } from '../../types/clinicalInteractions.types';
import { useSnackbar } from 'notistack';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../services/api';
import FdbClinicalPanel from './FdbClinicalPanel';
import UnifiedPatientInfoDialog, { PatientInfoTab } from '../common/UnifiedPatientInfoDialog';
import DinLink from '../common/DinLink';
import DrugImageThumbnail from '../common/DrugImageThumbnail';
import RxImageThumbnail from '../common/RxImageThumbnail';
import { formatDateForDisplay } from '../../utils/formatters';
import { useFdbClinicalData } from '../../hooks/queries/useFdbClinicalData';
import PatientAllergiesCard from '../common/PatientAllergiesCard';
import PatientEdPrintDialog from './PatientEdPrintDialog';
import ClinicalInteractionsDialog from '../clinical-verification/ClinicalInteractionsDialog';
import { PatientNotesPanel } from '../patient-360/core/PatientNotesPanel/PatientNotesPanel';
import { DECLINED_REASONS } from '../../constants/counselingConstants';
import { ageFromDob, getTodayString, getNowDateTimeLocalString } from '../../utils/timeUtils';
import {
  ISEC_QUESTIONS,
  ISEC_LETTER_COLORS,
  ISEC_SUMMARY_KEY,
  ISEC_SUMMARY_LABEL,
} from '../../constants/isecQuestions';
import { EMPTY_ISEC_POINTS, mergeIsecPoints, toIsecPoints } from './counselingChecklist';
import type { CounselingChecklist } from './counselingChecklist';
import type { IsecPoints } from '../../constants/isecQuestions';
import { ISEC_POINT_IDS } from '../../constants/isecQuestions';
import IsecPointRows, { checkAllIsecPoints, setIsecPointNotes, toggleIsecPoint } from './IsecPointRows';
import { useBlockHidden } from '../../hooks/queries/usePharmacyVisibility';
import { COUNSELING_CHECK_ALL_VISIBILITY_KEY } from '../../visibility/visibilityCatalogue';
import { tintSuccessCard } from '../../theme/gridSurfaces';

// ---- Types ----

// Re-exported for existing consumers of the checklist shape.
export type { CounselingChecklist, CounselingChecklistItem } from './counselingChecklist';

/** Shape of order.metadata.clinicalVerification.drugVerifications[] entries we read. */
interface CvDrugVerification {
  rxNumber?: string | number;
  din?: string;
  medicationName?: string;
  clinicalNotes?: Record<string, string>;
  /** GP-1488: the per-ISEC-point checklist documented on the CV sign page. */
  isecPoints?: IsecPoints;
  verifiedBy?: string;
  verifiedAt?: string;
}

// ---- Props ----

export interface CounselingDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-filled data from the clinical verification flow */
  orderData: {
    pharmacyId: string;
    orderId?: string;
    orderNumber?: string;
    rxNumber?: string;
    patientName?: string;
    patientId?: string;
    patientDob?: string;
    patientSex?: string;
    allergies?: string;
    conditions?: string;
    drugBrand?: string;
    drugGeneric?: string;
    din?: string;
    form?: string;
    strength?: string;
    quantity?: number;
    daysSupply?: number;
    /** Billing-time reason when days supply fell outside the patient's cycle (UAT 2026-07-09). */
    daysSupplyMismatchReason?: string;
    sig?: string;
    doctorName?: string;
    doctorLicense?: string;
    doctorPhone?: string;
    patientNotes?: string;
    patientPhone?: string;
    patientAddress?: string;
    deliveryDate?: string;
    deliveryType?: string;
    /** Drug-drug interactions from clinical screening */
    drugInteractions?: DrugDrugInteraction[];
    /** Duplicate therapies from clinical screening */
    duplicateTherapies?: DuplicateTherapy[];
    /** Allergy alerts from clinical screening */
    allergyAlerts?: AllergyAlert[];
  };
  /** If editing an existing counseling record */
  existingRecordId?: string;
  existingChecklist?: CounselingChecklist;
  existingStatus?: string;
  existingCounselingBy?: string;
  existingMethod?: string;
  existingComments?: string;
  existingDeclinedReason?: string;
  existingAgentName?: string;
  existingAgentRelationship?: string;
  existingDateCounselled?: string;
  /** Where the counseling was initiated from (clinical_verification or task_queue) */
  counselingOrigin?: 'clinical_verification' | 'task_queue';
  /**
   * Live ISEC notes from an in-progress CV session (not yet persisted to order
   * metadata). When provided, the "documented at Clinical Verification" panel
   * shows these instead of fetching the order's last-saved metadata.
   */
  liveCvIsecNotes?: Record<string, string>;
  /**
   * Per-point ISEC documentation from an in-progress CV session — seeds the
   * checklist when there is no existing record, so a session documented on the
   * sign page opens pre-filled here (document once).
   */
  initialIsecPoints?: IsecPoints;
  /** Called after save/finalize */
  onComplete?: (result?: CounselingCompletionResult) => void;
}

/** Outcome handed to onComplete on finalize, so the opener (e.g. the CV dialog)
 *  can carry the session summary forward — document once (GP-928). */
export interface CounselingCompletionResult {
  status: 'accepted' | 'declined';
  summary: string;
}

/**
 * GP-1266 — preset send-to-Task-Queue reasons offered on "Save for Later".
 * The control is free-form (Autocomplete freeSolo), so these are just quick
 * picks; staff can type anything. Mirrors the "New Start", "Dose Change",
 * "First Fill" statuses the Clinical Verification queue surfaces under
 * Order/Source. The chosen reason is persisted on the queued task's
 * Troubleshooting Reason + Task Notes so the queue can be triaged by reason.
 */
export const QUEUE_REASON_OPTIONS = [
  'New Start',
  'First Fill',
  'Dose Change',
  'Renewal',
  'Refill',
  'Counseling Required',
];

// ---- Component ----

const CounselingDialog: React.FC<CounselingDialogProps> = ({
  open,
  onClose,
  orderData,
  existingRecordId,
  existingChecklist,
  existingStatus,
  existingCounselingBy,
  existingMethod,
  existingComments,
  existingDeclinedReason,
  existingAgentName,
  existingAgentRelationship,
  existingDateCounselled,
  counselingOrigin,
  liveCvIsecNotes,
  initialIsecPoints,
  onComplete,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth0();

  // State
  const [status, setStatus] = useState(existingStatus || 'accepted');
  const [counselingBy, setCounselingBy] = useState(existingCounselingBy || 'patient');
  const [method, setMethod] = useState(existingMethod || 'by_phone');
  // Per-ISEC-question documentation (v2). Existing records (legacy 13-key or
  // v2) are normalized via toIsecPoints; a fresh session opened from CV seeds
  // from the sign page's live points (document once).
  const [points, setPoints] = useState<IsecPoints>(() => {
    if (existingChecklist) return toIsecPoints(existingChecklist as any);
    if (initialIsecPoints) return { ...EMPTY_ISEC_POINTS, ...initialIsecPoints };
    return EMPTY_ISEC_POINTS;
  });
  // Which ISEC letter's question set is expanded (parity with the CV sign page).
  const [isecExpanded, setIsecExpanded] = useState<Record<string, boolean>>({});
  // Free-form session summary (GP-928) — persisted on the record's `comments`
  // column (so legacy records surface theirs here) and appended to the
  // patient-timeline clinical note when the session is finalized.
  //
  // GP-1154 "document once": the Summary / Counselling Note the pharmacist typed
  // on the Clinical Verification sign page is the SAME record as this field, so a
  // fresh session (no existing counseling record) seeds it from the CV summary —
  // otherwise the CV note only showed read-only in the "documented at CV" block
  // and was silently absent from the counseling record + patient clinical notes.
  // When opened from a live CV session the value is available synchronously via
  // liveCvIsecNotes; the persisted-metadata path is seeded by an effect below.
  const initialSummary =
    existingComments || (liveCvIsecNotes?.[ISEC_SUMMARY_KEY] || '').trim();
  const [summaryText, setSummaryText] = useState(initialSummary);
  // GP-1266 — reason/status captured when this item is sent to the Task Queue
  // via "Save for Later" (e.g. New Start, Dose Change, First Fill). Free-form.
  const [queueReason, setQueueReason] = useState('');
  const [saving, setSaving] = useState(false);
  // Guard against losing unsaved documentation on Esc/backdrop/X close: snapshot
  // the substantive fields at mount and confirm before discarding changes.
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const initialSnapshotRef = useRef<string>(
    JSON.stringify([
      existingChecklist
        ? toIsecPoints(existingChecklist as any)
        : initialIsecPoints
          ? { ...EMPTY_ISEC_POINTS, ...initialIsecPoints }
          : EMPTY_ISEC_POINTS,
      initialSummary,
      existingStatus || 'accepted',
    ]),
  );
  // Mount-time points/status, so the async CV-summary seed (below) can fold ONLY
  // the summary into the discard-guard baseline — never concurrent point/status
  // edits a pharmacist made while the order metadata was still loading (GP-1154).
  const baselinePointsRef = useRef(points);
  const baselineStatusRef = useRef(status);
  const baselineSummaryRef = useRef(initialSummary);
  /**
   * Re-stamp the discard-guard baseline from the three baseline refs. Both async
   * "document once" seeds (the CV summary and, GP-1488, the CV ISEC points) call
   * this after advancing their OWN ref, so whichever resolves second no longer
   * clobbers the other's seed back to its mount-time value.
   */
  const resnapshotBaseline = useCallback(() => {
    initialSnapshotRef.current = JSON.stringify([
      baselinePointsRef.current,
      baselineSummaryRef.current,
      baselineStatusRef.current,
    ]);
  }, []);

  // Kroll-style tracking fields
  // Parse existing declined reason:
  //  - "Left voicemail[ at <timestamp>]" → 'left_voicemail' (timestamp captured separately)
  //  - "Other: <text>" or any legacy free text → 'other'
  //  - an exact DECLINED_REASONS value → that value
  const [declinedReason, setDeclinedReason] = useState(() => {
    const existing = existingDeclinedReason || '';
    if (!existing) return '';
    if (existing.startsWith('Left voicemail')) return 'left_voicemail';
    if (DECLINED_REASONS.some((r) => r.value === existing)) return existing;
    return 'other';
  });
  const [declinedReasonOther, setDeclinedReasonOther] = useState(() => {
    const existing = existingDeclinedReason || '';
    if (existing.startsWith('Left voicemail')) return '';
    if (existing.startsWith('Other: ')) return existing.slice(7);
    if (existing && !DECLINED_REASONS.some((r) => r.value === existing)) return existing;
    return '';
  });
  // Timestamp for the "Left voicemail" reason — auto-fills to now, editable.
  const [voicemailTimestamp, setVoicemailTimestamp] = useState(() => {
    const existing = existingDeclinedReason || '';
    const match = existing.match(/^Left voicemail at (.+)$/);
    return match ? match[1] : getNowDateTimeLocalString();
  });
  const [agentName, setAgentName] = useState(existingAgentName || '');
  const [agentRelationship, setAgentRelationship] = useState(existingAgentRelationship || '');
  const [dateCounselled, setDateCounselled] = useState(
    existingDateCounselled || getTodayString(),
  );
  const [sessionStartedAt] = useState(new Date().toISOString());

  // DDI review dialog
  const [ddiDialogOpen, setDdiDialogOpen] = useState(false);
  // Counseling points collapsible (show first 3)
  const [counselingPointsExpanded, setCounselingPointsExpanded] = useState(false);

  // Patient education collapsible
  const [patientEdExpanded, setPatientEdExpanded] = useState(false);

  // Patient Info Dialog state (All Rx, Rx Images, Notes, Timeline, History)
  const [patientInfoDialogOpen, setPatientInfoDialogOpen] = useState(false);
  const [patientInfoInitialTab, setPatientInfoInitialTab] = useState<PatientInfoTab>('prescriptions');

  const openPatientInfoDialog = (tab: PatientInfoTab) => {
    setPatientInfoInitialTab(tab);
    setPatientInfoDialogOpen(true);
  };

  const hasDin = !!orderData.din;

  // FDB clinical data — shares TanStack Query cache with FdbClinicalPanel (no duplicate HTTP calls)
  const patientAge = useMemo(() => ageFromDob(orderData.patientDob), [orderData.patientDob]);

  const { counseling: fdbCounseling, patientEducation: fdbPatientEd, durScreen, dinFillDates, drugInfo } = useFdbClinicalData(
    orderData.din,
    {
      patientAge,
      patientId: orderData.patientId,
      pharmacyId: orderData.pharmacyId,
      patientGender: orderData.patientSex,
      enabled: hasDin,
    },
  );

  const counselingMessages = fdbCounseling.data?.counselingMessages ?? [];
  const eduMonographs = fdbPatientEd.data?.monographs ?? [];

  // Build DIN → most recent fill date lookup for DDI display
  const dinFillDateMap = useMemo((): Record<string, string> => {
    const map: Record<string, string> = {};
    if (dinFillDates) Object.assign(map, dinFillDates);
    return map;
  }, [dinFillDates]);

  // Use passed-in interactions (from Clinical Verification), or fall back to FDB DUR screen
  // Filter to only interactions involving the current Rx being counseled on
  const currentOrderGcn = drugInfo?.data?.gcn;
  const ddiList = useMemo((): DrugDrugInteraction[] => {
    // Match by DIN or by GCN (FDB may return a different DIN for the same GCN)
    const matchesCurrent = (drug: { din: string; gcn?: number }) =>
      drug.din === orderData.din || (currentOrderGcn && drug.gcn === currentOrderGcn);

    let allDdis: DrugDrugInteraction[];
    if (orderData.drugInteractions && orderData.drugInteractions.length > 0) {
      allDdis = orderData.drugInteractions;
    } else {
      const fdbDdis = durScreen?.data?.drugDrugInteractions;
      if (!fdbDdis || fdbDdis.length === 0) return [];
      allDdis = fdbDdis.map((ddi) => ({
        drug1: { din: ddi.drug1.din, name: ddi.drug1.name || ddi.drug1.brandName || ddi.drug1.genericName, gcn: ddi.drug1.gcn },
        drug2: { din: ddi.drug2.din, name: ddi.drug2.name || ddi.drug2.brandName || ddi.drug2.genericName, gcn: ddi.drug2.gcn },
        severityLevel: ddi.severity?.level ?? 3,
        description: ddi.description,
        clinicalEffect: ddi.clinicalEffect?.text,
        monographId: ddi.monographId,
        involvesCurrentOrder: !!(matchesCurrent(ddi.drug1) || matchesCurrent(ddi.drug2)),
      }));
    }
    // Only show interactions involving the current order's drug
    if (orderData.din) {
      const filtered = allDdis.filter(ddi => ddi.involvesCurrentOrder);
      // If DIN/GCN match found nothing but we have DDIs, show all (data mismatch fallback)
      if (filtered.length === 0 && allDdis.length > 0) return allDdis;
      return filtered;
    }
    return allDdis;
  }, [orderData.drugInteractions, durScreen?.data, orderData.din, currentOrderGcn]);
  const ddiCount = ddiList.length;

  // Count duplicate therapies for the button label — prefer passed-in data from clinical screening
  const duplicateTherapyCount = useMemo(() => {
    if (orderData.duplicateTherapies?.length) {
      return orderData.duplicateTherapies.filter(dt =>
        dt.drugs.some(d => d.isCurrentOrder || d.din === orderData.din),
      ).length;
    }
    return (durScreen?.data?.duplicateTherapies ?? [])
      .filter(dt => dt.drugs.some(d => d.din === orderData.din)).length;
  }, [orderData.duplicateTherapies, durScreen?.data?.duplicateTherapies, orderData.din]);

  const totalDrugGroups = useMemo(() => {
    const matchesCurrent = (drug: { din: string; gcn?: number }) =>
      drug.din === orderData.din || (currentOrderGcn != null && drug.gcn === currentOrderGcn);
    const otherDrugs = new Set<string>();
    ddiList.forEach((ddi, idx) => {
      const other = matchesCurrent(ddi.drug1) ? ddi.drug2 : ddi.drug1;
      otherDrugs.add(other?.din || other?.name || `unknown_${idx}`);
    });
    return otherDrugs.size + duplicateTherapyCount;
  }, [ddiList, orderData.din, currentOrderGcn, duplicateTherapyCount]);

  // Patient Ed print dialog
  const [printDialogOpen, setPrintDialogOpen] = useState(false);

  // Shared point handlers (same sticky auto-check as the CV sign page — GP-926).
  const handleTogglePoint = useCallback((id: string) => {
    setPoints((prev) => toggleIsecPoint(prev, id));
  }, []);

  const handlePointNotes = useCallback((id: string, notes: string) => {
    setPoints((prev) => setIsecPointNotes(prev, id, notes));
  }, []);

  const checkedCount = ISEC_POINT_IDS.filter((id) => points[id]?.checked).length;

  // PRX-207: one "Check all" for every counselling point (PharmAssist). Points
  // stay individually toggleable. Shown by default on a Fillware pharmacy only.
  const checkAllHidden = useBlockHidden(COUNSELING_CHECK_ALL_VISIBILITY_KEY, orderData.pharmacyId);
  const handleCheckAllPoints = useCallback(() => {
    setPoints((prev) => checkAllIsecPoints(prev));
  }, []);

  // Close requested via Esc / backdrop / the title-bar X (NOT save/finalize,
  // which call onClose directly after persisting). Confirm before discarding.
  const isDirty =
    JSON.stringify([points, summaryText, status]) !== initialSnapshotRef.current;
  const handleRequestClose = () => {
    if (saving) return;
    if (isDirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    onClose();
  };

  // Compute effective declined reason string for API
  const effectiveDeclinedReason = useMemo(() => {
    if (declinedReason === 'other') {
      return declinedReasonOther.trim() ? `Other: ${declinedReasonOther.trim()}` : '';
    }
    if (declinedReason === 'left_voicemail') {
      return voicemailTimestamp ? `Left voicemail at ${voicemailTimestamp}` : 'Left voicemail';
    }
    return declinedReason;
  }, [declinedReason, declinedReasonOther, voicemailTimestamp]);

  const handleSaveForLater = async () => {
    setSaving(true);
    try {
      const trackingFields = {
        declinedReason: status === 'declined' ? effectiveDeclinedReason : undefined,
        agentName: counselingBy !== 'patient' ? agentName : undefined,
        agentRelationship: counselingBy !== 'patient' ? agentRelationship : undefined,
        dateCounselled,
      };

      if (existingRecordId) {
        await api.patch(`/api/counseling/${orderData.pharmacyId}/${existingRecordId}`, {
          status: 'pending',
          counselingBy,
          method,
          checklist: points,
          comments: summaryText,
          ...trackingFields,
        });
      } else {
        await api.post('/api/counseling', {
          pharmacyId: orderData.pharmacyId,
          orderId: orderData.orderId,
          rxNumber: orderData.rxNumber,
          patientId: orderData.patientId,
          patientName: orderData.patientName,
          drugName: orderData.drugBrand || orderData.drugGeneric,
          din: orderData.din,
          doctorName: orderData.doctorName,
          status: 'pending',
          reason: 'new_rx',
          counselingBy,
          method,
          checklist: points,
          comments: summaryText,
          sessionStartedAt,
          ...trackingFields,
          // Task Queue wireframe fields
          entryMethod: counselingOrigin === 'clinical_verification' ? 'Clinical Verification Queue' : undefined,
          patientDob: orderData.patientDob,
          linkStatus: orderData.patientId ? 'linked' : 'unlinked',
          counselingOrigin: counselingOrigin || 'task_queue',
          // GP-1266: send-time reason surfaced on the queued task's
          // Troubleshooting Reason + Task Notes for triage.
          queueReason: queueReason.trim() || undefined,
        });
      }
      enqueueSnackbar('Counseling saved for later — added to Task Queue', { variant: 'info' });
      onClose();
    } catch (err: any) {
      enqueueSnackbar(`Failed to save: ${err.message}`, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    if (status === 'declined' && !effectiveDeclinedReason) {
      enqueueSnackbar('Please select a reason for declining counseling', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const startTime = new Date(sessionStartedAt).getTime();
      const sessionDurationSeconds = Math.round((Date.now() - startTime) / 1000);
      const trackingFields = {
        declinedReason: status === 'declined' ? effectiveDeclinedReason : undefined,
        agentName: counselingBy !== 'patient' ? agentName : undefined,
        agentRelationship: counselingBy !== 'patient' ? agentRelationship : undefined,
        dateCounselled,
        sessionDurationSeconds,
      };

      if (existingRecordId) {
        // Update then finalize
        await api.patch(`/api/counseling/${orderData.pharmacyId}/${existingRecordId}`, {
          status: status === 'declined' ? 'declined' : 'accepted',
          counselingBy,
          method,
          checklist: points,
          comments: summaryText,
          ...trackingFields,
        });
        await api.post(`/api/counseling/${orderData.pharmacyId}/${existingRecordId}/finalize`);
      } else {
        // Create as completed
        const { data } = await api.post('/api/counseling', {
          pharmacyId: orderData.pharmacyId,
          orderId: orderData.orderId,
          rxNumber: orderData.rxNumber,
          patientId: orderData.patientId,
          patientName: orderData.patientName,
          drugName: orderData.drugBrand || orderData.drugGeneric,
          din: orderData.din,
          doctorName: orderData.doctorName,
          status: status === 'declined' ? 'declined' : 'accepted',
          reason: 'new_rx',
          counselingBy,
          method,
          checklist: points,
          comments: summaryText,
          sessionStartedAt,
          ...trackingFields,
          // Task Queue wireframe fields
          entryMethod: counselingOrigin === 'clinical_verification' ? 'Clinical Verification Queue' : undefined,
          patientDob: orderData.patientDob,
          linkStatus: orderData.patientId ? 'linked' : 'unlinked',
          counselingOrigin: counselingOrigin || 'task_queue',
        });
        // Immediately finalize
        if (data?.data?.id) {
          await api.post(`/api/counseling/${orderData.pharmacyId}/${data.data.id}/finalize`);
        }
      }
      enqueueSnackbar('Counseling finalized successfully', { variant: 'success' });
      onComplete?.({
        status: status === 'declined' ? 'declined' : 'accepted',
        summary: summaryText.trim(),
      });
      onClose();
    } catch (err: any) {
      enqueueSnackbar(`Failed to finalize: ${err.message}`, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Calculate patient age from DOB
  const getAge = () => ageFromDob(orderData.patientDob) ?? '';

  const hasPatientContext = !!orderData.patientId && !!orderData.patientName && !!orderData.pharmacyId;

  // Compact prescriptions summary for left panel
  const patientNumber = orderData.patientId?.includes('_')
    ? orderData.patientId.split('_')[1]
    : orderData.patientId;

  const {
    data: compactPrescriptions,
    isLoading: prescriptionsLoading,
  } = useQuery({
    queryKey: ['patient', 'all-prescriptions', orderData.pharmacyId, patientNumber, 'active'],
    queryFn: async () => {
      const response = await api.get(
        `/api/rx/${orderData.pharmacyId}/patient/${patientNumber}/all-prescriptions`,
        { params: { filter: 'active' } }
      );
      if (response.data?.success) {
        return response.data.data || [];
      }
      throw new Error('Failed to fetch prescriptions');
    },
    enabled: !!orderData.pharmacyId && !!patientNumber && open,
    staleTime: 2 * 60 * 1000,
  });

  const formatCompactDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(2);
      return `${day}/${month}/${year}`;
    } catch {
      return '—';
    }
  };

  // rxNumber can be comma-separated (e.g. "2239525, 2172242")
  const currentRxNumbers = useMemo(() => {
    if (!orderData.rxNumber) return [];
    return orderData.rxNumber.split(',').map((r) => r.trim());
  }, [orderData.rxNumber]);

  // GP-928 "document once": surface the ISEC notes + summary already recorded on
  // the CV sign page for this drug, so the pharmacist doesn't re-document here.
  // When opened from a live CV session, the caller passes liveCvIsecNotes (the
  // sign page's unsaved state) — the persisted-metadata fetch is skipped.
  const { data: cvDrugVerifications } = useQuery({
    queryKey: ['order', 'cv-isec-notes', orderData.pharmacyId, orderData.orderId],
    queryFn: async () => {
      const res = await api.get(`/api/orders/${orderData.pharmacyId}/${orderData.orderId}`);
      const order = res.data?.data || res.data;
      return (order?.metadata?.clinicalVerification?.drugVerifications ??
        []) as CvDrugVerification[];
    },
    enabled: open && !!orderData.orderId && !!orderData.pharmacyId && liveCvIsecNotes === undefined,
    staleTime: 60 * 1000,
  });

  const cvIsec = useMemo(() => {
    let notes: Record<string, string>;
    let verifiedBy: string | undefined;
    let verifiedAt: string | undefined;
    // GP-1488: the per-point ISEC checklist documented on the CV sign page, for
    // the persisted (Task Queue) path — the live path gets it via
    // initialIsecPoints. Undefined means "no CV documentation for this drug".
    let cvPoints: IsecPoints | undefined;
    if (liveCvIsecNotes !== undefined) {
      notes = liveCvIsecNotes;
    } else {
      const list = cvDrugVerifications ?? [];
      const byRx = list.find(
        (v) => v.rxNumber != null && currentRxNumbers.includes(String(v.rxNumber)),
      );
      const byDin = orderData.din
        ? list.find((v) => v.din && String(v.din) === String(orderData.din))
        : undefined;
      // Only a positive rx/din match may attribute notes to this drug — a lone
      // unmatched entry could be a DIFFERENT drug's documentation (item added
      // post-CV, split sibling), which must never display as already done.
      const match = byRx || byDin;
      notes = match?.clinicalNotes || {};
      cvPoints = match?.isecPoints;
      verifiedBy = match?.verifiedBy;
      verifiedAt = match?.verifiedAt;
    }
    const letterNotes = ISEC_QUESTIONS.map((g) => ({
      letter: g.letter,
      title: g.title,
      value: notes[g.key],
    })).filter((n) => n.value && n.value.trim());
    const summary = (notes[ISEC_SUMMARY_KEY] || '').trim();
    return { letterNotes, summary, points: cvPoints, verifiedBy, verifiedAt };
  }, [liveCvIsecNotes, cvDrugVerifications, currentRxNumbers, orderData.din]);
  // The summary now seeds the editable Summary / Counselling Note field (below),
  // so the "documented at Clinical Verification" block no longer echoes it (that
  // would duplicate it). But the block still carries the CV provenance/attribution
  // (verifiedBy · verifiedAt), so it must render whenever the CV documented ANY
  // content — per-letter notes OR a summary-only note — otherwise a summary-only
  // CV record silently loses its "documented at Clinical Verification" signal.
  const hasCvIsec = cvIsec.letterNotes.length > 0 || !!cvIsec.summary;

  // GP-1154: persisted-metadata path (counseling opened from the Task Queue, not
  // a live CV session) — the CV summary arrives asynchronously via
  // cvDrugVerifications. Seed the editable Summary / Counselling Note once it
  // loads, but only for a fresh record and only while the field is untouched, so
  // the "document once" note carries through here too.
  const cvSummarySeededRef = useRef(false);
  React.useEffect(() => {
    if (cvSummarySeededRef.current) return;
    if (liveCvIsecNotes !== undefined) return; // live path already seeded synchronously
    if (existingRecordId || existingComments) return; // an existing record's own comments win
    if (!cvIsec.summary || summaryText.trim()) return;
    cvSummarySeededRef.current = true;
    setSummaryText(cvIsec.summary);
    // Fold only the seeded summary into the discard-guard baseline so seeding
    // alone isn't "dirty"; keep the ORIGINAL mount-time points/status so any
    // edits made before the summary resolved still register as unsaved.
    baselineSummaryRef.current = cvIsec.summary;
    resnapshotBaseline();
  }, [cvIsec.summary, liveCvIsecNotes, existingRecordId, existingComments, summaryText, resnapshotBaseline]);

  // GP-1488 (the reverse leg): ISEC points documented on the CV sign page must
  // not be re-ticked here either. The live CV path is seeded synchronously via
  // initialIsecPoints; opened from the Task Queue the points come from the
  // persisted order metadata, which resolves asynchronously — so fold them in
  // once it lands. Additive (mergeIsecPoints): anything already ticked on this
  // sheet, or restored from the record's own checklist, wins.
  const cvPointsSeededRef = useRef(false);
  React.useEffect(() => {
    if (cvPointsSeededRef.current) return;
    if (liveCvIsecNotes !== undefined) return; // live path already seeded
    const cvPoints = cvIsec.points;
    if (!cvPoints) return; // still loading, or this drug has no CV documentation
    const merged = mergeIsecPoints(points, cvPoints);
    cvPointsSeededRef.current = true;
    if (merged === points) return;
    setPoints(merged);
    // Fold the seeded points into the discard-guard baseline so seeding alone
    // isn't "unsaved changes".
    baselinePointsRef.current = merged;
    resnapshotBaseline();
  }, [cvIsec.points, liveCvIsecNotes, points, resnapshotBaseline]);

  return (
    <Dialog
      open={open}
      onClose={handleRequestClose}
      fullScreen
    >
      {/* Title bar */}
      <DialogTitle
        sx={{
          bgcolor: '#c5e1a5',
          py: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" component="span" fontWeight="bold">
            Counseling
          </Typography>
          <Typography variant="body2">
            Rx Number <strong>{orderData.rxNumber || 'N/A'}</strong>
          </Typography>
          <Typography variant="body2">
            Reason <strong>New Rx</strong>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Patient info navigation buttons — same as Clinical Verification */}
          {hasPatientContext && (
            <>
              <Tooltip title="All Rx">
                <IconButton size="small" onClick={() => openPatientInfoDialog('prescriptions')}>
                  <AllRxIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rx Images">
                <IconButton size="small" onClick={() => openPatientInfoDialog('images')}>
                  <RxImagesIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Notes">
                <IconButton size="small" onClick={() => openPatientInfoDialog('notes')}>
                  <NotesIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Timeline">
                <IconButton size="small" onClick={() => openPatientInfoDialog('timeline')}>
                  <TimelineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="History">
                <IconButton size="small" onClick={() => openPatientInfoDialog('history')}>
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            </>
          )}
          <IconButton onClick={handleRequestClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden' }}>
        {/* ---- LEFT PANEL: Patient/Rx Info ---- */}
        <Box
          sx={{
            width: '25%',
            minWidth: 240,
            borderRight: '1px solid',
            borderColor: 'divider',
            overflow: 'auto',
            p: 2,
          }}
        >
          {/* Patient Info */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: '#fffde7' }}>
            <Typography variant="subtitle2" color="text.secondary">
              Patient
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {orderData.patientName || 'N/A'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
              {orderData.patientDob && (
                <Typography variant="caption">
                  DOB: {formatDateForDisplay(orderData.patientDob)} ({getAge()})
                </Typography>
              )}
              {orderData.patientSex && (
                <Typography variant="caption">Sex: {orderData.patientSex}</Typography>
              )}
            </Box>
            {orderData.patientPhone && (
              <Typography variant="caption" display="block" sx={{ mt: 0.25 }}>
                Ph: {orderData.patientPhone}
              </Typography>
            )}
            {orderData.patientAddress && (
              <Typography variant="caption" display="block" sx={{ mt: 0.25 }}>
                {orderData.patientAddress}
              </Typography>
            )}
          </Paper>

          {/* Delivery Info */}
          {(orderData.deliveryDate || orderData.deliveryType) && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: '#e3f2fd' }}>
              <Typography variant="subtitle2" color="text.secondary">
                Delivery
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {orderData.deliveryDate && (
                  <Typography variant="caption">Date: {orderData.deliveryDate}</Typography>
                )}
                {orderData.deliveryType && (
                  <Typography variant="caption">Type: {orderData.deliveryType}</Typography>
                )}
              </Box>
            </Paper>
          )}

          {/* Patient Notes */}
          {orderData.patientNotes && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: '#fff9c4' }}>
              <Typography variant="caption" color="text.secondary">
                {orderData.patientNotes}
              </Typography>
            </Paper>
          )}

          {/* Allergies + Conditions — one shared card so PHOX-registered
              entries (GP-1151 medical profile) show alongside the Kroll/order-
              sourced data instead of being invisible here. */}
          <PatientAllergiesCard
            pharmacyId={orderData.pharmacyId}
            patientId={orderData.patientId}
            sx={{ mb: 1.5 }}
            editable
            showConditions
            showWeight
            showMedications
            conditionsText={orderData.conditions}
          />

          {/* Compact Prescriptions Summary */}
          {hasPatientContext && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: '#e8eaf6' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <MedicationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Active Medications
                  </Typography>
                  {compactPrescriptions && (
                    <Chip
                      label={compactPrescriptions.length}
                      size="small"
                      sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }}
                    />
                  )}
                </Box>
                <Button
                  size="small"
                  onClick={() => openPatientInfoDialog('prescriptions')}
                  sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 'auto', py: 0, px: 0.5 }}
                >
                  See More
                </Button>
              </Box>

              {prescriptionsLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1, gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">Loading...</Typography>
                </Box>
              ) : !compactPrescriptions || compactPrescriptions.length === 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No active prescriptions found
                </Typography>
              ) : (
                <TableContainer sx={{ maxHeight: 200 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.25, px: 0.5, bgcolor: '#e8eaf6' }}>
                          Fill Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.25, px: 0.5, bgcolor: '#e8eaf6' }}>
                          Drug
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {compactPrescriptions.map((rx: any) => {
                        const isCurrentRx = currentRxNumbers.includes(String(rx.RxNum));
                        const drugName = rx.BrandName || rx.GenericName || '—';
                        return (
                          <TableRow
                            key={rx.RxNum}
                            sx={{
                              bgcolor: isCurrentRx ? '#e3f2fd' : 'transparent',
                              '&:last-child td': { borderBottom: 0 },
                            }}
                          >
                            <TableCell sx={{ fontSize: '0.7rem', py: 0.25, px: 0.5, whiteSpace: 'nowrap' }}>
                              {formatCompactDate(rx.FillDate)}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.7rem', py: 0.25, px: 0.5 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: isCurrentRx ? 700 : 400,
                                  fontSize: '0.7rem',
                                  display: 'block',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: 160,
                                }}
                              >
                                {drugName}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          )}

          {/* Drug-Drug Interactions & Alerts — always visible */}
          {(() => {
            const ddiLoading = !orderData.drugInteractions?.length && durScreen?.isLoading;
            const hasInteractions = totalDrugGroups > 0;
            return (
              <Button
                fullWidth
                variant="outlined"
                color={hasInteractions ? 'warning' : 'success'}
                startIcon={ddiLoading ? <CircularProgress size={16} /> : <WarningIcon />}
                onClick={() => hasInteractions && setDdiDialogOpen(true)}
                disabled={ddiLoading}
                sx={{
                  mb: 1.5,
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  borderColor: hasInteractions ? '#ed6c02' : '#2e7d32',
                  bgcolor: hasInteractions ? 'rgba(237,108,2,0.04)' : 'rgba(46,125,50,0.04)',
                  '&:hover': { bgcolor: hasInteractions ? 'rgba(237,108,2,0.1)' : 'rgba(46,125,50,0.1)' },
                  cursor: hasInteractions ? 'pointer' : 'default',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Typography variant="body2" fontWeight={600} color={hasInteractions ? 'warning.main' : 'success.main'}>
                    {ddiLoading
                      ? 'Screening interactions...'
                      : hasInteractions
                        ? `${totalDrugGroups} Interaction${totalDrugGroups !== 1 ? 's' : ''} with ${orderData.drugBrand || orderData.drugGeneric || 'Current Rx'}`
                        : 'No Drug Interactions Found'}
                  </Typography>
                  {hasInteractions && (
                    <Chip
                      label={`${totalDrugGroups} drug${totalDrugGroups !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{ ml: 'auto', height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              </Button>
            );
          })()}

          {/* Drug Info - Green section with embedded image */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: tintSuccessCard }}>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {orderData.din && (
                <DrugImageThumbnail din={orderData.din} size={80} borderRadius={6} showPlaceholder expandable />
              )}
              {orderData.pharmacyId && orderData.patientId && currentRxNumbers[0] && (
                <RxImageThumbnail
                  pharmacyId={orderData.pharmacyId}
                  patientId={orderData.patientId}
                  rxNumber={currentRxNumbers[0]}
                  size={80}
                  borderRadius={6}
                  expandable
                />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {orderData.drugBrand && (
                  <Typography variant="body2" fontWeight="bold">
                    {orderData.drugBrand} {orderData.strength}
                  </Typography>
                )}
                {orderData.drugGeneric && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {orderData.drugGeneric}
                  </Typography>
                )}
                {orderData.din && (
                  <Box sx={{ mt: 0.5 }}>
                    <DinLink din={orderData.din} variant="caption" />
                  </Box>
                )}
                {orderData.form && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {orderData.form}
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>

          {/* Doctor Info - Purple section */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: '#f3e5f5' }}>
            <Typography variant="subtitle2" color="text.secondary">
              Doctor
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {orderData.doctorName || 'N/A'}
            </Typography>
            {orderData.doctorLicense && (
              <Typography variant="caption" display="block">
                Lic #: {orderData.doctorLicense}
              </Typography>
            )}
            {orderData.doctorPhone && (
              <Typography variant="caption" display="block">
                Ph: {orderData.doctorPhone}
              </Typography>
            )}
          </Paper>

          {/* Qty / Days Supply */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 3 }}>
              {orderData.quantity != null && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Qty
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {orderData.quantity}
                  </Typography>
                </Box>
              )}
              {orderData.daysSupply != null && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Days Supply
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {orderData.daysSupply}
                  </Typography>
                </Box>
              )}
            </Box>
            {orderData.daysSupplyMismatchReason && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}>
                Days supply differs from the patient's cycle — billing reason: {orderData.daysSupplyMismatchReason}
              </Typography>
            )}
          </Paper>

          {/* Sig */}
          {orderData.sig && (
            <Paper
              variant="outlined"
              sx={{ p: 1.5, bgcolor: '#e3f2fd', fontFamily: 'monospace' }}
            >
              <Typography variant="body2">{orderData.sig}</Typography>
            </Paper>
          )}
        </Box>

        {/* ---- MIDDLE PANEL: Counseling Form ---- */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {/* Status / By / Method dropdowns */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Counseling</InputLabel>
              <Select
                value={status}
                label="Counseling"
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="accepted">Accepted</MenuItem>
                <MenuItem value="declined">Declined</MenuItem>
                {/* "Pending" is no longer offered as a manual choice — use "Save for Later"
                    to queue a pending record. Still rendered when editing an already-pending
                    record so the dropdown displays its current value correctly. */}
                {status === 'pending' && <MenuItem value="pending">Pending</MenuItem>}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>By</InputLabel>
              <Select
                value={counselingBy}
                label="By"
                onChange={(e) => setCounselingBy(e.target.value)}
              >
                <MenuItem value="patient">Patient</MenuItem>
                <MenuItem value="caregiver">Caregiver</MenuItem>
                <MenuItem value="representative">Representative</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Method</InputLabel>
              <Select
                value={method}
                label="Method"
                onChange={(e) => setMethod(e.target.value)}
              >
                <MenuItem value="by_phone">By Phone</MenuItem>
                <MenuItem value="in_person">In Person</MenuItem>
                <MenuItem value="video">Video</MenuItem>
                <MenuItem value="written">Written</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              type="date"
              label="Date Counselled"
              value={dateCounselled}
              onChange={(e) => setDateCounselled(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160 }}
            />

            <Chip
              label={`${checkedCount}/${ISEC_POINT_IDS.length}`}
              color={checkedCount === ISEC_POINT_IDS.length ? 'success' : 'default'}
              size="small"
              sx={{ alignSelf: 'center' }}
            />
            {!checkAllHidden && (
              <Button
                size="small"
                variant="outlined"
                onClick={handleCheckAllPoints}
                disabled={checkedCount === ISEC_POINT_IDS.length}
                sx={{ alignSelf: 'center' }}
                data-testid="counseling-check-all"
              >
                Check all
              </Button>
            )}
          </Box>

          {/* Agent details — shown when counseling is not directly with patient */}
          {(counselingBy === 'caregiver' || counselingBy === 'representative') && (
            <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
              <TextField
                size="small"
                label="Representative Name"
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
                sx={{ flex: 1 }}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Relationship</InputLabel>
                <Select
                  value={agentRelationship}
                  label="Relationship"
                  onChange={(e) => setAgentRelationship(e.target.value)}
                >
                  <MenuItem value="spouse">Spouse</MenuItem>
                  <MenuItem value="parent">Parent</MenuItem>
                  <MenuItem value="child">Child (Adult)</MenuItem>
                  <MenuItem value="guardian">Legal Guardian</MenuItem>
                  <MenuItem value="poa">Power of Attorney</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          <Divider sx={{ mb: 1.5 }} />

          {/* FDB Drug Counseling Points — collapsed, show first 3 */}
          {counselingMessages.length > 0 && (
            <Paper
              variant="outlined"
              sx={{
                p: 1.5, mb: 2,
                borderLeft: '4px solid #1976d2',
                bgcolor: 'rgba(25,118,210,0.04)',
                borderRadius: 1,
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                onClick={() => setCounselingPointsExpanded(!counselingPointsExpanded)}
              >
                <CounselingPointsIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                <Typography variant="subtitle2" fontWeight={700}>
                  Drug Counseling Points
                </Typography>
                <Chip
                  label={counselingMessages.length}
                  size="small"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
                {counselingPointsExpanded
                  ? <ExpandLessIcon sx={{ ml: 'auto', fontSize: 18, color: 'text.secondary' }} />
                  : <ExpandMoreIcon sx={{ ml: 'auto', fontSize: 18, color: 'text.secondary' }} />
                }
              </Box>
              {/* Always show first 3 */}
              {counselingMessages.slice(0, 3).map((msg) => (
                <Box key={msg.code} sx={{ mt: 0.5, pl: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.4, fontSize: '0.8rem' }}>
                    {msg.professionalMessage}
                  </Typography>
                </Box>
              ))}
              {/* Remaining items — collapsed */}
              <Collapse in={counselingPointsExpanded} timeout="auto">
                {counselingMessages.slice(3).map((msg) => (
                  <Box key={msg.code} sx={{ mt: 0.5, pl: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.4, fontSize: '0.8rem' }}>
                      {msg.professionalMessage}
                    </Typography>
                    {msg.patientMessage !== msg.professionalMessage && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontStyle: 'italic', display: 'block', mt: 0.15, fontSize: '0.7rem' }}
                      >
                        Patient: {msg.patientMessage}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Collapse>
              {!counselingPointsExpanded && counselingMessages.length > 3 && (
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{ display: 'block', mt: 0.5, pl: 1, cursor: 'pointer', fontSize: '0.7rem' }}
                  onClick={() => setCounselingPointsExpanded(true)}
                >
                  + {counselingMessages.length - 3} more points
                </Typography>
              )}
            </Paper>
          )}

          {/* ISEC notes documented at Clinical Verification (GP-928 "document once") */}
          {hasCvIsec && (
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 2,
                borderLeft: '4px solid #198754',
                bgcolor: 'rgba(25,135,84,0.04)',
                borderRadius: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                <Typography variant="subtitle2" fontWeight={700}>
                  ISEC — documented at Clinical Verification
                </Typography>
                {cvIsec.verifiedBy && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    {cvIsec.verifiedBy}
                    {cvIsec.verifiedAt ? ` · ${formatDateForDisplay(cvIsec.verifiedAt)}` : ''}
                  </Typography>
                )}
              </Box>
              {cvIsec.letterNotes.map((n) => (
                <Box key={n.letter} sx={{ mt: 0.5, pl: 1 }}>
                  <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.7rem' }}>
                    {n.letter} — {n.title}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                    {n.value}
                  </Typography>
                </Box>
              ))}
              {/* The CV Summary / Counselling Note now seeds the editable field
                  below (GP-1154) rather than being echoed read-only here. */}
            </Paper>
          )}

          {/* Per-ISEC-question points — checkbox + note beside each, exactly the
              same layout as the CV sign page (OCP round-2 feedback) */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid="counseling-isec-points">
            {ISEC_QUESTIONS.map((group) => {
              const groupChecked = group.questions.filter((q) => points[q.id]?.checked).length;
              const groupComplete = groupChecked === group.questions.length;
              return (
                <Paper key={group.key} variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
                  {/* Letter header — chevron expands this letter's question set */}
                  <Box
                    sx={{
                      px: 1,
                      py: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      bgcolor: 'grey.50',
                      borderBottom: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: 1,
                        bgcolor: groupComplete ? 'success.main' : ISEC_LETTER_COLORS[group.key],
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        flexShrink: 0,
                      }}
                    >
                      {groupComplete ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : group.letter}
                    </Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {group.title}
                    </Typography>
                    <Chip
                      label={`${groupChecked}/${group.questions.length}`}
                      size="small"
                      color={groupComplete ? 'success' : 'default'}
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  </Box>

                  {/* Per-question rows — shared with the CV sign page */}
                  <Box sx={{ py: 0.5 }}>
                    <IsecPointRows
                      questions={group.questions}
                      points={points}
                      onToggle={handleTogglePoint}
                      onNotes={handlePointNotes}
                    />
                  </Box>
                </Paper>
              );
            })}
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {status === 'declined' && (
            <Box sx={{ mt: 1.5 }}>
              <Alert severity="warning" sx={{ mb: 1 }}>
                Counseling declined. Please select a reason below.
              </Alert>
              <FormControl fullWidth size="small" required>
                <InputLabel>Reason for Declining</InputLabel>
                <Select
                  value={declinedReason}
                  label="Reason for Declining"
                  onChange={(e) => {
                    const value = e.target.value;
                    setDeclinedReason(value);
                    if (value !== 'other') setDeclinedReasonOther('');
                    // Auto-fill the voicemail timestamp to "now" when the reason is chosen.
                    if (value === 'left_voicemail' && !voicemailTimestamp) {
                      setVoicemailTimestamp(getNowDateTimeLocalString());
                    }
                  }}
                >
                  {DECLINED_REASONS.map((r) => (
                    <MenuItem key={r.value} value={r.value}>
                      {r.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {declinedReason === 'other' && (
                <TextField
                  fullWidth
                  size="small"
                  label="Specify reason"
                  placeholder="Enter custom reason..."
                  value={declinedReasonOther}
                  onChange={(e) => setDeclinedReasonOther(e.target.value)}
                  sx={{ mt: 1 }}
                  required
                />
              )}
              {declinedReason === 'left_voicemail' && (
                <TextField
                  fullWidth
                  size="small"
                  type="datetime-local"
                  label="Voicemail left at"
                  value={voicemailTimestamp}
                  onChange={(e) => setVoicemailTimestamp(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          )}

          {/* Medication Information Sheet */}
          {eduMonographs.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                  onClick={() => setPatientEdExpanded(!patientEdExpanded)}
                >
                  <PatientEdIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  <Typography variant="subtitle2" fontWeight={700}>
                    Medication Information Sheet
                  </Typography>
                  {patientEdExpanded ? (
                    <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  )}
                </Box>
                <Button
                  size="small"
                  startIcon={<PrintIcon />}
                  onClick={() => setPrintDialogOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Print
                </Button>
              </Box>
              <Collapse in={patientEdExpanded} timeout="auto">
                <Paper
                  variant="outlined"
                  sx={{ p: 2, maxHeight: 300, overflow: 'auto', borderRadius: 1 }}
                >
                  {eduMonographs.map((mono) =>
                    mono.sections.map((sec, idx) => (
                      <Box key={`${mono.monographId}-${idx}`} sx={{ mb: 1.5 }}>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color="primary.main"
                          sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                        >
                          {sec.section}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-line', mt: 0.25, lineHeight: 1.6 }}
                        >
                          {sec.text}
                        </Typography>
                      </Box>
                    )),
                  )}
                </Paper>
              </Collapse>
            </Box>
          )}

          {/* Summary / Counselling Note — free-form, at the very bottom (GP-928).
              Mirrors the CV sign page's summary field; posted to the patient's
              clinical notes when the session is finalized. */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              {ISEC_SUMMARY_LABEL}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              size="small"
              placeholder="Document the counselling session once — saved with this counseling record and posted to the patient's clinical notes when finalized."
              value={summaryText}
              onChange={(e) => setSummaryText(e.target.value)}
            />
          </Box>
        </Box>

        {/* ---- RIGHT PANEL: Notes & Clinical Reference ---- */}
        <Box
          sx={{
            width: '30%',
            minWidth: 280,
            borderLeft: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* FDB Clinical Reference (when DIN available) */}
          {hasDin && (
            <Box sx={{ flex: '0 0 auto', maxHeight: '40%', overflow: 'auto', bgcolor: '#fafafa' }}>
              <FdbClinicalPanel
                din={orderData.din}
                patientDob={orderData.patientDob}
                patientId={orderData.patientId}
                pharmacyId={orderData.pharmacyId}
                patientSex={orderData.patientSex}
              />
            </Box>
          )}
          {/* Patient Notes Panel (always visible) */}
          {orderData.patientId && (
            <Box sx={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto', borderTop: hasDin ? '1px solid' : 'none', borderColor: 'divider' }}>
              <PatientNotesPanel
                patientId={orderData.patientId}
                defaultTab="clinical"
                compact
                maxHeight="none"
                showHeader={false}
                defaultTags={[
                  ...(orderData.orderNumber ? [`order:${orderData.orderNumber}`] : []),
                  'counseling',
                  ...(orderData.rxNumber ? [`rx:${orderData.rxNumber}`] : []),
                ]}
                relatedOrderId={orderData.orderId}
                orderNumber={orderData.orderNumber}
                relatedPrescriptionId={orderData.rxNumber}
                showOnOrder
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* Footer actions */}
      <DialogActions sx={{ px: 3, py: 1.5, justifyContent: 'space-between' }}>
        {/* GP-1266: capture WHY this item is being sent to the Task Queue so the
            queue can be triaged by reason. Free-form (freeSolo) with quick picks. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Autocomplete
            freeSolo
            size="small"
            options={QUEUE_REASON_OPTIONS}
            value={queueReason}
            onInputChange={(_, v) => setQueueReason(v)}
            disabled={saving}
            sx={{ width: 220 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Task Queue reason"
                placeholder="e.g. New Start, Dose Change"
              />
            )}
          />
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSaveForLater}
            disabled={saving}
            color="inherit"
          >
            {saving ? <CircularProgress size={16} /> : 'Save for Later'}
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {eduMonographs.length > 0 && (
            <Button
              startIcon={<PrintIcon />}
              onClick={() => setPrintDialogOpen(true)}
              color="inherit"
            >
              Print Patient Info
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<CheckCircleIcon />}
            onClick={handleFinalize}
            disabled={saving}
            color="primary"
          >
            {saving ? <CircularProgress size={16} /> : 'Finalize Counseling'}
          </Button>
        </Box>
      </DialogActions>

      {/* Drug-Drug Interactions Review Dialog (shared component) */}
      <ClinicalInteractionsDialog
        open={ddiDialogOpen}
        onClose={() => setDdiDialogOpen(false)}
        ddiList={ddiList}
        dinFillDates={dinFillDateMap}
        currentDin={orderData.din}
        currentGcn={currentOrderGcn}
        duplicateTherapies={
          orderData.duplicateTherapies?.length
            ? orderData.duplicateTherapies.filter(dt => dt.drugs.some(d => d.isCurrentOrder || d.din === orderData.din))
            : (durScreen?.data?.duplicateTherapies ?? [])
                .filter(dt => dt.drugs.some(d => d.din === orderData.din))
                .map(dt => ({
                  therapyClass: dt.therapyClass,
                  drugs: dt.drugs.map(d => ({
                    din: d.din,
                    name: d.name,
                    isCurrentOrder: d.din === orderData.din,
                  })),
                }))
        }
        allergyAlerts={orderData.allergyAlerts}
        drugName={orderData.drugBrand || orderData.drugGeneric || 'Current Rx'}
        loading={false}
      />

      {/* Patient Education Print Dialog */}
      <PatientEdPrintDialog
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        drugName={orderData.drugBrand || orderData.drugGeneric || 'Medication'}
        din={orderData.din}
        patientName={orderData.patientName}
        monographs={eduMonographs}
        counselingMessages={counselingMessages}
      />

      {/* Patient Info Dialog (All Rx, Rx Images, Notes, Timeline, History) */}
      {hasPatientContext && (
        <UnifiedPatientInfoDialog
          open={patientInfoDialogOpen}
          onClose={() => setPatientInfoDialogOpen(false)}
          initialTab={patientInfoInitialTab}
          patientId={orderData.patientId!}
          patientName={orderData.patientName!}
          pharmacyId={orderData.pharmacyId}
          orderId={orderData.orderId}
          orderNumber={orderData.orderNumber}
          highlightRxNumbers={orderData.rxNumber ? [orderData.rxNumber] : undefined}
        />
      )}

      {/* Unsaved-changes guard for Esc / backdrop / X close */}
      <Dialog open={discardConfirmOpen} onClose={() => setDiscardConfirmOpen(false)} maxWidth="xs">
        <DialogTitle>Discard unsaved counseling documentation?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Your checklist selections and summary have not been saved. Use{' '}
            <strong>Save for Later</strong> to keep them, or discard to close without saving.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardConfirmOpen(false)}>Keep Editing</Button>
          <Button
            color="error"
            onClick={() => {
              setDiscardConfirmOpen(false);
              onClose();
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CounselingDialog;
